/**
 * Plans & billing configuration — the single source of truth for what is free
 * vs. Pro, the subscription price points, and the master billing flag.
 *
 * Activation model: everything in the billing system is wired but DORMANT until
 * `BILLING_ENABLED` is true. While it's false, no paywalls or upgrade CTAs are
 * shown and every signed-in user keeps full access (today's behavior). When the
 * Stripe account is live, set the env vars (see BILLING_SETUP.md) and flip
 * VITE_BILLING_ENABLED=true — no code change required.
 */

/** Subscription plan tier stored on the user profile (written only by the Stripe webhook). */
export type PlanTier = 'free' | 'pro';

/** Stripe subscription status, mirrored onto the user profile by the webhook. */
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | null;

/** Where a user's Pro access comes from. 'org' is reserved for the future B2B/white-label seat model. */
export type PlanSource = 'self' | 'org';

/** Game modes as used by the store / nav. Kept as a local union to avoid an import cycle with the store. */
export type GameMode =
  | 'training'
  | 'table-simulator'
  | 'practice'
  | 'speed-drill'
  | 'edge-calculator'
  | 'simulator'
  | 'statistics';

function flag(value: string | undefined): boolean {
  return value === 'true' || value === '1';
}

/**
 * Master switch for the entire billing/paywall system. Default OFF.
 * While false: no gating, no upgrade CTAs — every user has full access.
 */
export const BILLING_ENABLED = flag(import.meta.env.VITE_BILLING_ENABLED as string | undefined);

/**
 * Whether the public self-serve "Sign up free" path is shown (B2C funnel).
 * Independent of BILLING_ENABLED so signup can open before/after billing goes live.
 * Default OFF keeps the app invite-only until you choose to open it.
 */
export const OPEN_SIGNUP_ENABLED = flag(import.meta.env.VITE_OPEN_SIGNUP_ENABLED as string | undefined);

/** Stripe publishable key (safe to expose). Empty until billing is configured. */
export const STRIPE_PUBLISHABLE_KEY =
  (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) || '';

/** Stripe Price IDs for each billing interval. Set in Vercel/.env once products exist in Stripe. */
export const STRIPE_PRICE_IDS = {
  month: (import.meta.env.VITE_STRIPE_PRICE_MONTHLY as string | undefined) || '',
  year: (import.meta.env.VITE_STRIPE_PRICE_ANNUAL as string | undefined) || '',
} as const;

export type BillingInterval = keyof typeof STRIPE_PRICE_IDS;

/** Free-trial length (days) applied to individual Pro checkouts. 0 disables the trial. */
export const TRIAL_DAYS = 14;

/**
 * Display pricing for the UI. The actual charge is whatever the Stripe Price is
 * configured to — keep these in sync with Stripe. Editing the numbers here is a
 * one-line change.
 */
export const PRICING = {
  month: { amount: 14.99, label: '$14.99', period: '/mo', priceId: STRIPE_PRICE_IDS.month },
  year: {
    amount: 119,
    label: '$119',
    period: '/yr',
    priceId: STRIPE_PRICE_IDS.year,
    /** Savings vs. paying monthly for a year, for the "save X%" badge. */
    savingsPct: Math.round((1 - 119 / (14.99 * 12)) * 100),
  },
} as const;

/** Modes available to everyone on the free tier. */
export const FREE_MODES: readonly GameMode[] = ['training', 'edge-calculator'];

/** Modes that require an active Pro subscription. */
export const PRO_MODES: readonly GameMode[] = [
  'table-simulator',
  'practice',
  'speed-drill',
  'simulator',
  'statistics',
];

/** Marketing copy for the Pro tier — used by the pricing table and upgrade modal. */
export const PRO_FEATURES: readonly string[] = [
  'Full multi-seat table simulator with live AI opponents',
  'Speed counting drills with timed scoring',
  'Monte Carlo risk simulator (win rate, hourly, risk of ruin)',
  'Practice scenarios for specific decisions',
  'Advanced statistics, XP & leveling, and achievements',
  'Customizable rules, penetration, and counting systems',
];

// ---------------------------------------------------------------------------
// B2B / white-label (team) plans
// ---------------------------------------------------------------------------
// An admin subscribes to a team tier; their invited users (seats) inherit Pro
// via the admin's org entitlement (written to adminConfigs/{adminUid} by the
// webhook). Seat limits are enforced server-side in sendInvite. White-label is
// contact-sales (no self-serve price).

export type TeamTierKey = 'team' | 'group' | 'academy';

export interface TeamTier {
  key: TeamTierKey;
  label: string;
  seatLimit: number;
  blurb: string;
  monthly: { amount: number; label: string; priceId: string };
  annual: { amount: number; label: string; priceId: string };
}

// Static reads (not computed `import.meta.env[key]`) so Vite reliably inlines them.
const str = (v: unknown): string => (v as string | undefined) || '';

export const TEAM_TIERS: readonly TeamTier[] = [
  {
    key: 'team',
    label: 'Team',
    seatLimit: 10,
    blurb: 'AP crews and small coaching groups.',
    monthly: { amount: 79, label: '$79', priceId: str(import.meta.env.VITE_STRIPE_PRICE_TEAM_MONTHLY) },
    annual: { amount: 790, label: '$790', priceId: str(import.meta.env.VITE_STRIPE_PRICE_TEAM_ANNUAL) },
  },
  {
    key: 'group',
    label: 'Group',
    seatLimit: 30,
    blurb: 'Coaches running cohorts.',
    monthly: { amount: 179, label: '$179', priceId: str(import.meta.env.VITE_STRIPE_PRICE_GROUP_MONTHLY) },
    annual: { amount: 1790, label: '$1,790', priceId: str(import.meta.env.VITE_STRIPE_PRICE_GROUP_ANNUAL) },
  },
  {
    key: 'academy',
    label: 'Academy',
    seatLimit: 100,
    blurb: 'Large coaching programs.',
    monthly: { amount: 399, label: '$399', priceId: str(import.meta.env.VITE_STRIPE_PRICE_ACADEMY_MONTHLY) },
    annual: { amount: 3990, label: '$3,990', priceId: str(import.meta.env.VITE_STRIPE_PRICE_ACADEMY_ANNUAL) },
  },
] as const;

/** White-label / enterprise tier — contact sales, no self-serve checkout. */
export const WHITE_LABEL = {
  label: 'White-label',
  blurb: 'Your brand and logo on your players’ app, 250+ seats. For creators and large programs.',
  priceFrom: 'from $499/mo',
} as const;

/** Human-readable labels for each mode (also used by the nav). */
export const MODE_LABELS: Record<GameMode, string> = {
  training: 'Hand Trainer',
  'table-simulator': 'Full Table',
  practice: 'Scenarios',
  'speed-drill': 'Speed Drill',
  'edge-calculator': 'Edge Calc',
  simulator: 'Risk Sim',
  statistics: 'Stats',
};
